import { z } from 'zod';
import {
   type ActionPriorityEnum,
   type ActionStatusEnum,
   type ActionTypeEnum,
   type CasePriorityEnum,
   type CaseStatusEnum,
   type ComplaintPriorityEnum,
   type ComplaintSourceEnum,
   type ComplaintStatusEnum,
   type InfluencingCapacityEnum,
   Risks,
   type RisksEnum,
   type SupplierPriorityEnum,
   type SupplierSectorEnum,
   type SupplierTierEnum,
   type SupplierTypeValueEnum,
   type TurnOverEnum,
} from './enums';
import type { SupplyChainSupplierEmission } from './supplierEmissions';
import {
   type Attachment,
   type Comment,
   type EntityBase,
   type EntityBaseStrict,
   type Group,
   type ISO8601DateOnlyString,
   type Organisation,
   type SupplyChainQuestionnaire,
   type User,
} from 'types/models';
import { type GeoJsonPoint } from 'types/sequelize-geo';

export type {
   SupplierEmissionFieldName,
   SupplierEmissionMethod,
   SupplyChainSupplierEmission,
   SupplyChainSupplierEmissionPayload,
   SupplyChainSupplierEmissionSourceOption,
} from './supplierEmissions';

export type SupplierQuestionnaireDetailTarget =
   | {
        kind: 'dispatch';
        dispatchId: number;
     }
   | {
        kind: 'response';
        questionnaireResponseId: number;
     }
   | {
        kind: 'shareRequest';
        shareRequestId: number;
     };

export type QuestionnaireRiskCategoryOption = {
   slug: RisksEnum;
   label: string;
   groupLabel: string;
   groupSortOrder: number;
};

export const EntityType = {
   SUPPLIER: 'SUPPLIER',
   NEWS: 'NEWS',
   COMPLAINT: 'COMPLAINT',
   CASE: 'CASE',
   ACTION: 'ACTION',
   QUESTIONNAIRE: 'QUESTIONNAIRE',
} as const;
export type EntityTypeEnum = (typeof EntityType)[keyof typeof EntityType];

export interface SupplierAddress {
   street: string | null;
   zip: string | null;
   city: string | null;
   countryCode: string | null;
   regionCode: string | null;
   geography: GeoJsonPoint | null;
}

export interface SupplierContact {
   id: number;
   firstName: string | null;
   lastName: string | null;
   name?: string | null;
   email: string | null;
   position?: number | null;
   telephone?: string | null;
   language?: string | null;
}

export interface SupplierRiskScore {
   id: number;
   supplierId: number;
   organisationId: number;
   countryRiskScore: number | null;
   sectorRiskScore: number | null;
   sectorRiskDescription: string | null;
   sectorIndustryName: string | null;
   countrySectorRiskScore: number | null;
   initialRiskScore: number | null;
   concreteRiskScore: number | null;
   activeRiskScore: number | null;
   humanRightsRiskScore: number | null;
   environmentalRiskScore: number | null;
   governanceRiskScore: number | null;
   categoryScores: Record<string, number | null> | null;
   sectorCategoryScores?: Record<string, number | null> | null;
   concreteCategoryScores: Record<string, number | null> | null;
   lastConcreteAssessmentAt: string | null;
   concreteAssessmentSource: string | null;
   countryCode: string | null;
   naceCode: string | null;
   needsReview: boolean;
   reviewReason: string | null;
   calculatedAt: string | null;
   documentReductionScores: Record<string, number> | null;
}

export interface SupplierOrganisationPeriod {
   id: number;
   validFrom: string;
   validTo: string | null;
   organisationId: number;
   supplierId: number;
   supplier: Supplier;
}

export interface Supplier extends EntityBase {
   address?: SupplierAddress;
   addressId?: number;
   code?: string | null;
   contactPeople?: SupplierContact[];
   currencyCode?: string | null;
   defaultLanguage?: string;
   identificationNumber?: string | null;
   influencingCapacity?: InfluencingCapacityEnum | null;
   labels?: Label[];
   logo?: string | null;
   naceCode?: string | null;
   /** True when `naceCode` came from an AI classification (explicit apply in a form, or the bulk "Suggest NACE codes" action). */
   isNaceAiGenerated?: boolean | null;
   /** When the AI NACE proposal was reviewed (approved or rejected). `null` while AI-generated = pending review. */
   naceProposalReviewedAt?: string | null;
   naceClass?: { code: string; name: string | null } | null;
   nSupplier: SupplierTierEnum | null;
   name: string;
   organisations: (Pick<Organisation, 'id' | 'name'> & { supplier_organisations: SupplierOrganisationPeriod })[];
   priority?: SupplierPriorityEnum | null;
   emissions?: SupplyChainSupplierEmission[];
   riskScores?: SupplierRiskScore[];
   sector?: SupplierSectorEnum | null;
   tags?: string[];
   tier?: SupplierTierEnum | null;
   turnOverOwnBusinessUnit?: number | null;
   turnover?: TurnOverEnum | null;
   type?: SupplierTypeValueEnum | null;
   url?: string | null;
   validFrom: ISO8601DateOnlyString | null;
   validTo?: ISO8601DateOnlyString | null;
   responsibleUsers?: User[];
   responsibleGroups?: Group[];
   responsiblePeople?: Array<User | Group>;
   actions?: SupplierAction[];
   cases?: SupplierCase[];
   complaints?: SupplierComplaint[];
}

/** A supplier whose AI-suggested NACE code is awaiting review (derived client-side from the supplier list). */
export interface PendingNaceProposal {
   id: number;
   name: string;
   naceCode: string | null;
   sector: SupplierSectorEnum | null;
}

/** Minimal supplier info needed to generate a pending AI NACE proposal for it (bulk "Suggest NACE codes"). */
export interface NaceGenerationTarget {
   id: number;
   name: string;
   /** Current NACE code — targets with a code are skipped so an existing value is never overwritten. */
   naceCode: string | null;
   /** Current sector — only filled from the AI suggestion when not already set. */
   sector: SupplierSectorEnum | null;
}

/** Request body for the read-only AI NACE classification endpoint (POST /v2/ai/supplier-classification/suggest). */
export interface SuggestClassificationRequest {
   /** Mode A: when provided, the server hydrates name/url/address from the DB and ignores any client-supplied ones. */
   supplierId?: number;
   /** Mode B (ad-hoc): required if supplierId is omitted. */
   name?: string;
   url?: string | null;
   address?: {
      street?: string | null;
      zip?: string | null;
      city?: string | null;
      countryCode?: string | null;
      regionCode?: string | null;
   } | null;
}

export interface SupplierClassificationAlternative {
   code: string;
   label: string;
   sector: SupplierSectorEnum | null;
   /** Cosine similarity 0–1, rounded to 2 decimals. */
   score: number;
}

/** Suggested NACE Rev. 2.1 class + internal sector. Read-only — never written to the supplier by the endpoint. */
export interface SupplierClassification {
   /** Chosen NACE Rev. 2.1 class code, e.g. "24.10". */
   code: string;
   label: string;
   sector: SupplierSectorEnum | null;
   /** Gated confidence in [0, 1], rounded to 2 decimals. */
   confidence: number;
   /** Routing band derived from confidence + signal strength. Drives the UI, not the raw confidence. */
   band: 'high' | 'medium' | 'low';
   /** One-line, user-safe justification for the pick. */
   rationale: string;
   /** Provenance of the description that was classified. */
   source: 'structured-metadata' | 'body-text' | 'subpage' | 'web-search' | 'name-address-only';
   signalStrength: 'high' | 'medium' | 'low';
   /** Up to 2 runner-up candidates, for a "pick one of these" UX on low confidence. */
   alternatives?: SupplierClassificationAlternative[];
}

export interface SupplierAction extends EntityBase {
   assignees?: Array<User | Group>;
   attachments?: Array<Attachment>;
   cases?: SupplierCase[];
   evaluation?: string;
   labels?: Label[];
   name: string;
   description: string;
   priority?: ActionPriorityEnum;
   riskCategory: RisksEnum[];
   status: ActionStatusEnum;
   suppliers: Supplier[];
   validFrom: ISO8601DateOnlyString | null;
   validTo: ISO8601DateOnlyString | null;
   type: ActionTypeEnum | null;
}

export interface SupplierCase extends EntityBase {
   actions?: SupplierAction[];
   complaints?: SupplierComplaint[];
   evaluation: CaseEvaluationItem[];
   evaluationState?: Record<string, CaseRiskEvaluation>;
   labels?: Label[];
   name: string;
   priority: CasePriorityEnum;
   riskCategory: RisksEnum[];
   status: CaseStatusEnum;
   suppliers: Supplier[];
   assignees?: Array<User | Group>;
   attachments?: Array<Attachment>;
   date: string | null;
}

export interface Label {
   id: number;
   name: string;
   color: string;
   slug?: string;
}

export type ComplaintLabel = Label;
export type CaseLabel = Label;

export type CaseEvaluationStatus = Extract<CaseStatusEnum, 'OPEN' | 'IN_PROGRESS' | 'CLOSED'>;

export interface CaseEvaluationFactors {
   magnitude?: number;
   scope?: number;
   irreversibility?: number;
   probability?: number;
}

export interface CaseEvaluationComputedScore {
   severity: number;
   evaluationScore: number;
}

export interface CaseRiskEvaluation extends EntityBaseStrict {
   riskCategoryKey: string;
   status: CaseEvaluationStatus;
   factors: CaseEvaluationFactors;
   currentRiskScore?: number;
   updatedRiskScore?: number;
   evaluationScore?: number;
   computed?: CaseEvaluationComputedScore;
}

export interface CaseEvaluationItem extends EntityBaseStrict {
   slug: string;
   label: string;
   value?: number;
   status: CaseEvaluationStatus;
   currentRiskScore?: number;
   currentRiskScoresPerSupplier?: Array<{
      supplierId: number;
      supplierName: string;
      score: number | null;
   }>;
   updatedRiskScore?: number;
   evaluationScore?: number;
   factors?: CaseEvaluationFactors;
}

export interface ActionEvaluationItem extends EntityBaseStrict {
   slug: string;
   label: string;
   value?: number;
   status: ActionStatusEnum;
}

export interface SupplierComplaint extends EntityBase {
   comments?: Comment[];
   description: string;
   internalDescription?: string;
   labels?: Label[];
   cases?: SupplierCase[];
   name: string;
   date: string | null;
   otherDesc?: string;
   priority: ComplaintPriorityEnum;
   riskCategory: RisksEnum[];
   source: ComplaintSourceEnum;
   status: ComplaintStatusEnum;
   supplierId: number;
   supplier: Supplier;
   assignees?: Array<User | Group>;
   attachments?: Array<Attachment>;
}

export type SupplierQuestionnaire = EntityBase;

const localizedStringObjectSchema = z
   .looseObject({
      default: z.string().min(1).optional(),
   })
   .catchall(z.string())
   .refine((value) => Object.keys(value).length > 0);

const localizedStringSchema = z.union([z.string(), localizedStringObjectSchema]);
export type LocalizedString = z.infer<typeof localizedStringSchema>;

const questionnaireRiskSlugSchema = z.enum(Object.values(Risks) as [RisksEnum, ...RisksEnum[]]);

const surveyElementSchema = z.looseObject({
   type: z.string().nullish(),
   name: z.string().nullish(),
   riskCategory: questionnaireRiskSlugSchema.nullish(),
   title: localizedStringSchema.nullish(),
});

export const surveyJsonSchema = z.looseObject({
   title: localizedStringSchema.nullish(),
   elements: z.array(surveyElementSchema).nullish(),
   pages: z
      .array(
         z.looseObject({
            name: z.string().nullish(),
            elements: z.array(surveyElementSchema).nullish(),
         })
      )
      .nullish(),
});

export type SupplierOption = {
   id: number;
   name?: string | null;
   countryCode?: string | null;
   riskScore?: number | null;
   sectorCode?: string | null;
   sectorLabel?: string | null;
   tier?: SupplierTierEnum | null;
   turnOverOwnBusinessUnit?: number | null;
   influencingCapacity?: InfluencingCapacityEnum | null;
   priority?: SupplierPriorityEnum | null;
   labels?: Label[];
   contactPeople?: Array<SupplierContact>;
};

export type QuestionnaireOverviewStatus = 'SENT' | 'DRAFT' | 'REQUESTED' | 'REJECTED' | 'REVIEW_PENDING' | 'COMPLETED' | 'EXPIRED';

export interface QuestionnaireOverviewRow {
   id: number;
   questionnaireId: number;
   questionnaireResponseId?: number | null;
   detailTarget?: SupplierQuestionnaireDetailTarget | null;
   name: string;
   dueDate: Date | null;
   questionnaireRiskScore?: number | null;
   status: QuestionnaireOverviewStatus;
   submittedAt: Date | null;
   surveyJson?: SupplyChainQuestionnaire['surveyJson'];
   supplierId?: number | null;
   supplierName?: string | null;
   organisationId?: number | null;
   sharedQuestionnaire?: {
      requestId?: number;
      sourceResponseId?: number | null;
      sourceOrganisationId?: number | null;
      sourceOrganisationName?: string | null;
      status: 'REQUESTED' | 'ACCEPTED' | 'REJECTED';
      sourceExpired?: boolean;
   };
}

export type QuestionnaireAnswerFile = {
   id?: number;
   name: string;
   type?: string | null;
   mimeType?: string | null;
   sizeBytes?: number | null;
   url?: string | null;
   questionKey?: string | null;
};

interface QuestionnaireAnswerMatrixRow {
   questionName?: string;
   statement: string;
   answer: string;
   impactScore?: number | null;
   impactCategory?: string | null;
   riskCategory?: string | null;
   riskCategoryLabel?: string;
}

interface QuestionnaireAnswerMatrixDropdownAnswer {
   label: string;
   value: string;
}

interface QuestionnaireAnswerMatrixDropdownRow {
   statement: string;
   answers: QuestionnaireAnswerMatrixDropdownAnswer[];
}

export interface QuestionnaireAnswerRow {
   id: string;
   questionName?: string;
   question: string;
   section?: string;
   pageId?: string | null;
   pageTitle?: string | null;
   questionOrder?: number;
   answer?: string;
   answerText?: string;
   answerComment?: string;
   answerKind?: 'empty' | 'text' | 'list' | 'key_value_list' | 'files' | 'matrix' | 'matrix_dropdown';
   answerItems?: string[];
   answerEntries?: Array<{ label: string; value: string; files?: QuestionnaireAnswerFile[] }>;
   answerFiles?: QuestionnaireAnswerFile[];
   matrixRows?: QuestionnaireAnswerMatrixRow[];
   matrixDropdownRows?: QuestionnaireAnswerMatrixDropdownRow[];
   impactScore: number | null;
   impactCategory?: string | null;
   riskCategory?: string | null;
   riskCategoryLabel?: string;
}

export interface QuestionnaireAnswerImpactEntry {
   impactScore: number | null;
   impactCategory?: string | null;
   section?: string | null;
}

export interface SupplyChainItemComment extends EntityBaseStrict {
   id: number;
   user: string;
   comment: string;
   date: Date;
}
